import { StarRounded } from '@mui/icons-material'
import AddIcon from '@mui/icons-material/Add'
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded'
import InfoIcon from '@mui/icons-material/Info'
import { CircularProgress, IconButton, Stack, Typography } from '@mui/material'
import { NodeType } from '../../../definitions/Nodes'
import { INodeDescription } from '../../../definitions/NodesDescription'
import NodesIcon from '../nodes/NodesIcon'

interface P {
    nodeDescription: INodeDescription<NodeType>
    mode: string
    borderPanel: string
    heightNodePanel: string
    isFavorite: boolean
    onDragStart: (e: React.DragEvent<HTMLDivElement>, nodeType: NodeType) => void
    onAddNode: (nodeType: NodeType) => void
    onInfoNode: (desc: INodeDescription<NodeType>) => void
    onUpdateFavorite: (desc: INodeDescription<NodeType>) => void
    updateFavoriteStatus: string
    loadingNodeType: NodeType | null
}

const NodeCard = ({ nodeDescription, mode, borderPanel, heightNodePanel, isFavorite, onDragStart, onAddNode, onInfoNode, onUpdateFavorite, updateFavoriteStatus, loadingNodeType }: P) => {
    const disabled = nodeDescription.type === 'exe_subFlow'
    return (
        <Stack
            onDragStart={disabled ? undefined : e => onDragStart(e, nodeDescription.type)}
            draggable={!disabled}
            direction='row'
            width={250}
            height={heightNodePanel}
            spacing={1}
            justifyContent='center'
            alignItems='center'
            marginBottom={1}
            sx={{
                'backgroundColor': 'var(--xy-node-color, var(--xy-node-color-default))',
                'border': mode === 'dark'
                    ? `${borderPanel} solid #2C2C2C`
                    : `${borderPanel} solid #F6F6F6`,
                'borderRadius': 1,
                'cursor': disabled ? 'not-allowed' : 'grab',
                '&:hover': disabled
                    ? {}
                    : {
                            transform: 'scale(1.02)',
                            transition: 'transform 0.3s ease-in-out',
                            boxShadow: 'var(--xy-node-boxshadow-hover-default)'
                        }
            }}>
            <Stack
                flexDirection='row'
                alignItems='center'
                justifyContent='center'
                sx={{
                    backgroundColor: mode === 'dark' ? '#2C2C2C' : '#F6F6F6',
                    height: heightNodePanel,
                    borderTopLeftRadius: 'inherit',
                    borderBottomLeftRadius: 'inherit'
                }}>
                <DragIndicatorRoundedIcon sx={{ fontSize: 15, paddingRight: borderPanel }} color='action' />
            </Stack>
            <Stack flexDirection='row' alignItems='center'>
                <NodesIcon nodeType={nodeDescription.type} size={32} />
            </Stack>
            <Stack height='100%' alignItems='flex-start' justifyContent='space-around' paddingY={1}>
                <Stack>
                    <Typography variant='subtitle2' fontWeight='bold' color={theme => disabled ? theme.palette.text.disabled : undefined}>
                        {nodeDescription.name}
                    </Typography>
                </Stack>
                <Stack overflow='hidden'>
                    <Typography
                        color={mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'}
                        variant='caption'
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                        }}>
                        {(!nodeDescription.description || nodeDescription.description.length === 0)
                            ? `Placeholder description for node ${nodeDescription.name}...Lorem ipsum dolor sit amet consectetur adipisicing elit.`
                            : nodeDescription.description}
                    </Typography>
                </Stack>
                <Stack flexDirection='row' alignSelf='flex-end' paddingRight={1} gap={1}>
                    <IconButton
                        disabled={disabled}
                        size='small'
                        onClick={() => onAddNode(nodeDescription.type)}
                        sx={{ '&.MuiIconButton-root': { padding: 0.3 } }}>
                        <AddIcon fontSize='small' />
                    </IconButton>
                    <IconButton
                        disabled={disabled}
                        size='small'
                        onClick={() => onInfoNode(nodeDescription)}
                        sx={{ '&.MuiIconButton-root': { padding: 0.3 } }}>
                        <InfoIcon fontSize='small' />
                    </IconButton>
                    <IconButton
                        disabled={disabled}
                        size='small'
                        onClick={() => onUpdateFavorite(nodeDescription)}
                        sx={{
                            '&.MuiIconButton-root': { padding: 0.3 },
                            'color': isFavorite ? '#FBC02D' : undefined
                        }}>
                        {(updateFavoriteStatus === 'loading' && loadingNodeType === nodeDescription.type)
                            ? <CircularProgress size={16} />
                            : <StarRounded fontSize='small' />}
                    </IconButton>
                </Stack>
            </Stack>
        </Stack>
    )
}

export default NodeCard
